'use client';

import { useState, useEffect } from 'react';
import type { EntityOwnership, OwnershipRole } from '@/lib/command-center/types';
import { RACI_ROLE_LABELS } from '@/lib/command-center/types';

interface OwnershipPanelProps {
  entityId: string;
  entityName?: string;
  workspaceId: string;
  onOwnershipChange?: () => void;
  compact?: boolean;
}

export function OwnershipPanel({
  entityId,
  entityName,
  workspaceId,
  onOwnershipChange,
  compact = false,
}: OwnershipPanelProps) {
  const [ownership, setOwnership] = useState<EntityOwnership[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [adding, setAdding] = useState(false);

  // Form state
  const [ownerName, setOwnerName] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [role, setRole] = useState<OwnershipRole>('responsible');
  const [isPrimary, setIsPrimary] = useState(false);
  const [notes, setNotes] = useState('');

  // Fetch ownership
  const fetchOwnership = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`/api/secure/command-center/entities/${entityId}/ownership`);
      if (!response.ok) {
        throw new Error('Failed to fetch ownership');
      }
      const data = await response.json();
      setOwnership(data.ownership || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch ownership');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOwnership();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityId]);

  // Add ownership
  const handleAddOwnership = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ownerName.trim() || !ownerEmail.trim()) return;

    try {
      setAdding(true);
      const response = await fetch(`/api/secure/command-center/entities/${entityId}/ownership`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          owner_name: ownerName.trim(),
          owner_email: ownerEmail.trim(),
          role,
          is_primary: isPrimary,
          notes: notes.trim() || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to add ownership');
      }

      // Reset form and refresh
      setOwnerName('');
      setOwnerEmail('');
      setRole('responsible');
      setIsPrimary(false);
      setNotes('');
      setShowAddForm(false);
      await fetchOwnership();
      onOwnershipChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add ownership');
    } finally {
      setAdding(false);
    }
  };

  // Remove ownership
  const handleRemoveOwnership = async (ownershipId: string) => {
    if (!confirm('Remove this ownership record?')) return;

    try {
      const response = await fetch(
        `/api/secure/command-center/entities/${entityId}/ownership?ownership_id=${ownershipId}`,
        { method: 'DELETE' }
      );

      if (!response.ok) {
        throw new Error('Failed to remove ownership');
      }

      await fetchOwnership();
      onOwnershipChange?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove ownership');
    }
  };

  // Group by role for RACI display
  const ownershipByRole = ownership.reduce(
    (acc, o) => {
      if (!acc[o.role]) acc[o.role] = [];
      acc[o.role].push(o);
      return acc;
    },
    {} as Record<OwnershipRole, EntityOwnership[]>
  );

  if (compact) {
    const primaryOwner = ownership.find((o) => o.is_primary);
    return (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-foreground-tertiary">Owner:</span>
        {primaryOwner ? (
          <span className="text-foreground">{primaryOwner.owner_name}</span>
        ) : ownership.length > 0 ? (
          <span className="text-foreground">{ownership[0].owner_name}</span>
        ) : (
          <span className="text-foreground-muted italic">No owner</span>
        )}
        {ownership.length > 1 && (
          <span className="text-foreground-muted">+{ownership.length - 1}</span>
        )}
      </div>
    );
  }

  return (
    <div className="bg-surface/50 rounded-xl border border-border-muted overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border-muted">
        <div>
          <h3 className="text-sm font-medium text-foreground">Ownership & RACI</h3>
          {entityName && <p className="text-xs text-foreground-tertiary">for {entityName}</p>}
        </div>
        <button
          onClick={() => setShowAddForm(!showAddForm)}
          className="px-3 py-1.5 text-sm text-accent hover:text-accent hover:bg-accent-muted rounded-lg transition-colors"
        >
          {showAddForm ? 'Cancel' : '+ Add Owner'}
        </button>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mt-4 px-3 py-2 text-sm text-red-400 bg-red-900/20 border border-red-500/30 rounded">
          {error}
        </div>
      )}

      {/* Add form */}
      {showAddForm && (
        <form onSubmit={handleAddOwnership} className="p-4 border-b border-border-muted bg-background/30">
          <div className="space-y-3">
            {/* Name and Email */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">
                  Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  placeholder="John Doe"
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-tertiary mb-1">
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  placeholder="john@example.com"
                  className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none"
                  required
                />
              </div>
            </div>

            {/* Role */}
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">RACI Role</label>
              <div className="grid grid-cols-4 gap-2">
                {(Object.keys(RACI_ROLE_LABELS) as OwnershipRole[]).map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    className={`px-2 py-2 text-xs rounded-lg border transition-colors ${
                      role === r
                        ? `${RACI_ROLE_LABELS[r].color} border-current bg-current/10`
                        : 'text-foreground-tertiary border-border-muted hover:border-border/30'
                    }`}
                  >
                    <div className="font-medium">{RACI_ROLE_LABELS[r].label[0]}</div>
                    <div className="text-[10px] opacity-70">{RACI_ROLE_LABELS[r].label}</div>
                  </button>
                ))}
              </div>
              <p className="mt-1 text-xs text-foreground-muted">{RACI_ROLE_LABELS[role].description}</p>
            </div>

            {/* Primary and Notes */}
            <div className="flex items-start gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isPrimary}
                  onChange={(e) => setIsPrimary(e.target.checked)}
                  className="w-4 h-4 rounded border-border bg-background text-accent focus:ring-ring"
                />
                <span className="text-sm text-foreground-secondary">Primary owner</span>
              </label>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-xs text-foreground-tertiary mb-1">Notes (optional)</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional context..."
                rows={2}
                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-foreground-muted focus:border-accent focus:outline-none resize-none"
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={adding || !ownerName.trim() || !ownerEmail.trim()}
              className="w-full px-4 py-2 text-sm font-medium bg-accent text-accent-foreground rounded-lg hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {adding ? 'Adding...' : 'Add Owner'}
            </button>
          </div>
        </form>
      )}

      {/* Ownership list */}
      <div className="max-h-96 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-foreground-tertiary">
            <div className="inline-block w-5 h-5 border-2 border-border border-t-orange rounded-full animate-spin" />
          </div>
        ) : ownership.length === 0 ? (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-cream/5 mb-3">
              <svg className="w-6 h-6 text-foreground-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                />
              </svg>
            </div>
            <p className="text-foreground-tertiary text-sm">No owners assigned</p>
            <p className="text-foreground-muted text-xs mt-1">
              Add owners to track responsibility
            </p>
          </div>
        ) : (
          <div className="divide-y divide-border-muted">
            {/* RACI summary */}
            <div className="p-4 grid grid-cols-4 gap-2 bg-background/30">
              {(['responsible', 'accountable', 'consulted', 'informed'] as OwnershipRole[]).map((r) => {
                const count = ownershipByRole[r]?.length || 0;
                return (
                  <div
                    key={r}
                    className={`text-center p-2 rounded-lg ${count > 0 ? 'bg-background/50' : 'bg-background/20'}`}
                  >
                    <div className={`text-2xl font-bold ${count > 0 ? RACI_ROLE_LABELS[r].color : 'text-foreground-muted'}`}>
                      {RACI_ROLE_LABELS[r].label[0]}
                    </div>
                    <div className="text-xs text-foreground-muted">{count}</div>
                  </div>
                );
              })}
            </div>

            {/* Owner list */}
            {ownership.map((owner) => (
              <div key={owner.id} className="p-4 hover:bg-cream/5 transition-colors">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium text-foreground">
                        {owner.owner_name}
                      </span>
                      {owner.is_primary && (
                        <span className="px-1.5 py-0.5 text-xs bg-accent-muted text-accent rounded">
                          Primary
                        </span>
                      )}
                      <span
                        className={`px-1.5 py-0.5 text-xs rounded ${
                          RACI_ROLE_LABELS[owner.role].color
                        } bg-current/10`}
                      >
                        {RACI_ROLE_LABELS[owner.role].label}
                      </span>
                    </div>
                    <div className="text-xs text-foreground-tertiary">{owner.owner_email}</div>
                    {owner.notes && (
                      <p className="text-xs text-foreground-tertiary mt-1">{owner.notes}</p>
                    )}
                    <div className="text-xs text-foreground-muted mt-1">
                      Added {new Date(owner.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRemoveOwnership(owner.id)}
                    className="p-1.5 text-foreground-muted hover:text-red-400 hover:bg-red-900/20 rounded transition-colors"
                    title="Remove owner"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default OwnershipPanel;
